import {
  AssignLeadMutation,
  DynamicallyFilteredLeadsQuery,
  useAllSalesUsersQuery,
  useAppDataQuery,
} from 'graphql/bindings/AdminBindings'
import { Autocomplete, OptionType, SpinnerButton, useSnackbar } from '@forgeglobal/design-framework'
import React, { useState } from 'react'

import Modal, { useModal } from './Modal'

export const useAssignModal = () => {
  const { isOpen, open, close } = useModal()
  const [accountId, setAccountId] = useState<string | null>(null)

  return {
    accountId,
    isOpen,
    open: (accountId: string) => {
      setAccountId(accountId)
      open()
    },
    close: () => {
      setAccountId(null)
      close()
    },
  }
}

interface AssignModalProps {
  open: boolean
  onClose: () => void
  account: DynamicallyFilteredLeadsQuery['dynamicQuery']['data'][0] | undefined
  assignLead: (accountId: string, assigneeId: number) => Promise<AssignLeadMutation['assignLead'] | undefined>
}

const AssignModal: React.FC<AssignModalProps> = ({ open, onClose, account, assignLead }) => {
  const { enqueueSnackbar } = useSnackbar()
  const { data: appDataQuery } = useAppDataQuery()
  const { data: allSalesUsersData } = useAllSalesUsersQuery()

  const { currentAdmin } = appDataQuery || {}
  const defaultAssigneeId = currentAdmin?.id

  const [assigneeId, setAssigneeId] = useState(defaultAssigneeId)
  const resetAssigneeId = () => setAssigneeId(defaultAssigneeId)
  const handleClose = () => {
    resetAssigneeId()
    onClose()
  }

  const assigneeOptions = [
    { label: `Me (${currentAdmin?.name})`, value: currentAdmin?.id },
    ...(allSalesUsersData?.allUsersByCapability || [])
      .filter(({ id }) => id !== currentAdmin?.id)
      .map(({ id: value, name: label }) => ({ label, value })),
  ]

  const currentAssigneeOption = assigneeOptions.find(({ value }) => value === assigneeId)

  return (
    <Modal open={open} onClose={handleClose} title={'Assign profiling'}>
      <Autocomplete
        options={assigneeOptions}
        onChange={option => option && setAssigneeId((option as OptionType<number>).value)}
        label="Assignee"
        value={currentAssigneeOption}
      />
      <SpinnerButton
        fullWidth
        color="primary"
        loading={false}
        caption="Assign"
        onClick={() => {
          handleClose()

          const accountId = account?.id

          if (assigneeId && accountId) {
            assignLead(accountId, assigneeId).catch(() => {})
          } else {
            enqueueSnackbar("We couldn't find that account. Please try again.", { variant: 'error' })
          }
        }}
      />
    </Modal>
  )
}

export default AssignModal
